import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button as MuiButton,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import PageHeader1 from '../../../components/common/PageHeader1';
import { useDispatch, useSelector } from 'react-redux';
import { fetchReview } from '../../../Redux/Reducers/ReviewCourseReducer';
import { usePermissions } from '../../../context/AuthContext';

function CourseListReview() {
    const { can } = usePermissions();
    const dispatch = useDispatch();
    const [open, setOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });
    const course = useSelector((state) => state?.ReviewCourseReducer?.review);
    const pagination = useSelector((state) => state?.ReviewCourseReducer?.pagination);

    useEffect(() => {
        const { page, pageSize } = paginationModel;
        dispatch(fetchReview({ page: page + 1, limit: pageSize }));
    }, [paginationModel, dispatch]);

    function deleteCourse(id) {
        axios
            .delete(`${process.env.REACT_APP_BASEURL}/admin/review/${id}`)
            .then(() => {
                const { page, pageSize } = paginationModel;
                dispatch(fetchReview({ page: page + 1, limit: pageSize }));
            })
            .catch((error) => {
                console.error("Error deleting course:", error);
            });
    }

    const handleClickOpen = (userId) => {
        setSelectedUserId(userId);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setSelectedUserId(null);
    };

    const handleDelete = () => {
        if (selectedUserId) {
            deleteCourse(selectedUserId);
        }
        handleClose();
    };

    const columns = [
        { field: 'SoNo', headerName: 'S.No', width: 150 },
        { field: 'categoryName', headerName: 'Category Name', width: 250 },
        { field: 'title', headerName: 'Course Name', width: 250 },
        { field: 'reviews', headerName: 'Number of reviews', width: 250 },
        { field: 'rating', headerName: 'Rating', width: 300 },
        { field: 'createdAt', headerName: 'Created At', width: 250 },
        { field: 'updatedAt', headerName: 'Updated At', width: 250 },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 150,
            renderCell: ({ row }) => {
                return (
                    <div>
                        {can('course_specific_reviews:edit') && (
                            <Tooltip title="Edit Review">
                                <IconButton component={Link} to={process.env.PUBLIC_URL + `/editReviewCourse/${row?.id}`}>
                                    <i className="icofont-edit text-info"></i>
                                </IconButton>
                            </Tooltip>
                        )}
                        {can('course_specific_reviews:delete') && (
                            <Tooltip title="Delete Review">
                                <IconButton component="button" onClick={() => handleClickOpen(row?.id)}>
                                    <i className="icofont-ui-delete text-danger"></i>
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                );
            },
        }
    ];

    const [filterModel, setFilterModel] = useState({
        items: [],
        quickFilterExcludeHiddenColumns: true,
        quickFilterValues: [''],
    });

    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

    const rows = Array.isArray(course)
        ? course?.map((item, index) => {
            const reviews = Array.isArray(item?.reviews) ? item.reviews : [];
            const averageRating =
                reviews.length > 0
                    ? (reviews.reduce((sum, review) => sum + (review.rating || 0), 0) / reviews.length).toFixed(1)
                    : "-";
            return {
                SoNo: paginationModel.page * paginationModel.pageSize + index + 1,
                id: item?._id,
                title: item?.courseId?.title ?? "-",
                categoryName: item?.courseId?.categoryId?.categoryName,
                reviews: reviews.length,
                rating: averageRating,
                createdAt: item?.createdAt
                    ? new Date(item?.createdAt).toLocaleString()
                    : "",
                updatedAt: item?.updatedAt
                    ? new Date(item?.updatedAt).toLocaleString()
                    : "",
            };
        })
        : [];

    return (
        <div className="body d-flex py-1">
            <div className="container-xxl">
                <PageHeader1
                    righttitle='Add Review'
                    link='/AddReviewCourse'
                    routebutton={can('course_specific_reviews:add')}
                    pagetitle='Course Specific Review List'
                />
                <div className="row g-0 mb-3">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-body">
                                <div id="myDataTable_wrapper" className="dataTables_wrapper dt-bootstrap5 no-footer">
                                    <div className="row">
                                        <div className="col-sm-12">
                                            <Box sx={{ width: 1 }}>
                                                <Box sx={{ height: 400, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                                    {loading ? (
                                                        <CircularProgress />
                                                    ) : (
                                                        <DataGrid
                                                            columns={columns}
                                                            rows={rows}
                                                            getRowId={(row) => row.id}
                                                            disableColumnFilter
                                                            disableDensitySelector
                                                            slots={{ toolbar: GridToolbar }}
                                                            filterModel={filterModel}
                                                            onFilterModelChange={(newModel) => setFilterModel(newModel)}
                                                            slotProps={{ toolbar: { showQuickFilter: true } }}
                                                            columnVisibilityModel={columnVisibilityModel}
                                                            onColumnVisibilityModelChange={(newModel) =>
                                                                setColumnVisibilityModel(newModel)
                                                            }
                                                            paginationMode="server"
                                                            rowCount={pagination?.totalDocs || 0}
                                                            pageSizeOptions={[10, 25, 50, 100]}
                                                            paginationModel={paginationModel}
                                                            onPaginationModelChange={setPaginationModel}
                                                        />
                                                    )}
                                                </Box>
                                            </Box>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Dialog
                open={open}
                onClose={handleClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Delete Confirmation"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Are you sure you want to delete this record?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MuiButton onClick={handleClose}>
                        Cancel
                    </MuiButton>
                    <MuiButton onClick={handleDelete} color="error" autoFocus>
                        Delete
                    </MuiButton>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default CourseListReview;